import React, { Children, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  BoxProps,
  Button,
  Card,
  Paper,
  Stack,
  styled,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Iconify from 'src/components/iconify';

export interface IFormLayoutProps extends BoxProps {
  title?: string;
  open?: boolean;
  detailForm?: React.ReactNode;
  rowEndNode?: React.ReactNode;
  method: UseFormReturn<any>;
  onReset?: VoidFunction;
  alignItems?: 'start' | 'end' | 'center' | 'flex-start' | 'flex-end';
  rowSx?: SxProps<Theme>;
  sx2?: SxProps<Theme>;
  formItemButton?: React.ReactNode;
  formItemMarked?: boolean;
  addBtn?: React.ReactElement;
  cols?: number;
  size?: number;
}

/**
 * FormLayout
 * SearchFormLayout: 검색영역 레이아웃
 * FormGroup: 검색영역 그룹
 * FormRow: 검색영역 Row
 * FormItem: 검색영역 Item
 * FormInnerContainer: 테두리 영역 스타일
 * FormLabel: 테두리 좌측 상단 라벨 스타일
 * StyledLoadingButton: 로딩 버튼 스타일
 */

export const SearchFormLayout = ({
  open = true,
  method,
  detailForm,
  children,
  onReset,
  addBtn,
  cols = 5,
}: IFormLayoutProps) => {
  const { reset } = method;
  const [expanded, setExpanded] = React.useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  // children없을 경우 예외처리:  children을 배열로 변환 후 사용
  const child = Children.toArray(children);

  // form 영역 아코디언 open/close
  useEffect(() => {
    setExpanded(open);
  }, [open]);

  // 검색
  const handleOnSubmit = (e: any) => {
    e.stopPropagation();
  };

  // 검색 초기화
  const handleOnReset = (e: any) => {
    e.stopPropagation();
    reset();
    onReset && onReset();
  };

  // 문자열에서 숫자 추출 후 더하는 함수
  const sumColSize = (input: string): number => {
    // 문자열에서 숫자만 추출
    const numbers = input.match(/\d+/g);
    // 추출된 숫자를 숫자형으로 변환한 후 더함
    const sum = numbers?.reduce((acc, num) => acc + parseInt(num, 10), 0);
    return sum || 0;
  };

  // form 레이아웃 중 1fr에 2개 form이 들어갈 경우 레이아웃
  const doubleSizeElements = [
    'RHFDateRangePicker',
    'RHFRadioGroup',
    'RHFSelectTextField',
    'RHFSelectDate',
  ];
  const splitDataBySize = () => {
    const result = [];
    let currentBatch: any[] = [];
    let currentSizeSum = 0;
    let currentCol = '';

    try {
      child.forEach((item: any) => {
        const size =
          item.props.size || doubleSizeElements.includes(item.props.children.type?.name) ? 2 : 1;
        if (currentSizeSum + size > cols) {
          if (sumColSize(currentCol) < cols) {
            for (let i = sumColSize(currentCol); i < cols; i++) {
              currentCol += '1fr ';
            }
          }
          result.push({
            list: currentBatch,
            cols: currentCol,
          });
          currentCol = '';
          currentBatch = [];
          currentSizeSum = 0;
        }
        currentCol += size + 'fr ';
        currentBatch.push(item);
        currentSizeSum += size;
      });
    } catch (e) {
      console.error('formLayout', e);
    }

    if (currentBatch.length > 0) {
      if (sumColSize(currentCol) < cols) {
        for (let i = sumColSize(currentCol); i < cols; i++) {
          currentCol += '1fr ';
        }
      }
      result.push({
        list: currentBatch,
        cols: currentCol,
      });
    }

    return result;
  };

  const items = splitDataBySize();

  return (
    <>
      <Card className={'search-area'}>
        <Accordion expanded={expanded} className={'search-area'}>
          <AccordionSummary
            id="search-area-basic"
            sx={{
              cursor: 'default !important',
              height: 64,
              '&.MuiAccordionSummary-root': {
                padding: '0 8px',
              },
            }}
          >
            {items.length > 0 && <SearchStack cols={items[0].cols}>{items[0].list}</SearchStack>}
          </AccordionSummary>
          {child.length > cols && (
            <AccordionDetails
              sx={{
                '&.MuiAccordionDetails-root': {
                  padding: '0 8px',
                  paddingBottom: 2,
                  minHeight: 52,
                },
              }}
            >
              {items.slice(1, items.length).map((item: any, i: number) => {
                return (
                  <SearchStack key={'search-stack-' + i} cols={item.cols}>
                    {item.list}
                  </SearchStack>
                );
              })}
              {detailForm && <AccordionDetails>{detailForm}</AccordionDetails>}
            </AccordionDetails>
          )}
        </Accordion>
      </Card>
      {child.length > cols && (
        <Button
          variant={'text'}
          color={'inherit'}
          sx={{ position: 'absolute', right: 16, px: 0, paddingLeft: 1, fontWeight: 'normal' }}
          onClick={() => setExpanded(!expanded)}
        >
          {!expanded ? (
            <>
              {t('common.button.detailSearchOpen')} <Iconify icon="iconamoon:arrow-down-2" />
            </>
          ) : (
            <>
              {t('common.button.detailSearchClose')} <Iconify icon="iconamoon:arrow-up-2" />
            </>
          )}
        </Button>
      )}
      <Stack direction={'row'} spacing={1} justifyContent={'center'} sx={{ p: 4 }}>
        <Tooltip title="Search" arrow={true}>
          <Button
            aria-label="search"
            type="submit"
            variant={'contained'}
            color={'primary'}
            onClick={handleOnSubmit}
          >
            {t('common.button.search')}
          </Button>
        </Tooltip>
        <Tooltip title="Reset" arrow={true}>
          <Button
            aria-label="reset"
            type="reset"
            variant={'outlined'}
            color={'inherit'}
            sx={{ background: theme.palette.background.default }}
            onClick={handleOnReset}
          >
            {t('common.button.reset')}
          </Button>
        </Tooltip>
        {addBtn}
      </Stack>
    </>
  );
};

export const FormGroup = ({ title, children, sx }: Partial<IFormLayoutProps>) => {
  const theme = useTheme();
  return (
    <Box sx={{ ...sx }}>
      <Typography variant="subtitle1" sx={{ borderBottom: `solid 2px ${theme.palette.divider}` }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0, alignItems: 'flex-end', flexFlow: 'wrap' }}>
        {children}
      </Box>
    </Box>
  );
};

export const FormRow = ({ children, rowSx, sx }: Partial<IFormLayoutProps>) => {
  return (
    <Box
      sx={{
        width: '100%',
        mt: 0,
        mb: 1,
        minHeight: '50px',
        display: 'flex',
        alignItems: 'center',
        '&:last-child': { mb: 0 },
        ...rowSx,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          width: '100%',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
          ...sx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const FormItem = ({
  title,
  children,
  sx,
  alignItems = 'center',
  sx2,
  formItemButton,
  formItemMarked = false,
}: Partial<IFormLayoutProps>) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'no-wrap',
        gap: 3,
        flex: '1 0 auto',
        alignItems: alignItems,
        minHeight: '37px',
      }}
    >
      {title && (
        <Stack
          sx={{
            flex: '1 0 100px',
            height: '100%',
            maxWidth: '100px',
            pt: '14px',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '19px',
              ...sx2,
            }}
          >
            <>
              {title}
              {formItemMarked && (
                <Typography sx={{ display: 'inline-block', color: 'red' }}>
                  &nbsp;&nbsp;*
                </Typography>
              )}
            </>
          </Typography>
          {formItemButton && formItemButton}
        </Stack>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minWidth: '150px',
          gap: 1,
          width: '100%',
          ...sx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// 테두리 + 라벨 스타일
export const FormInnerContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  position: 'relative',
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
}));
export const FormLabel = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(0, 1),
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: '-12px',
  left: '20px',
  background: theme.palette.background.paper,
}));
export const StyledLoadingButton = styled(LoadingButton)(() => ({
  minWidth: 100,
  height: 40,
}));
export const FooterButtonContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: 2,
}));

// FormInnerContainer를 row로 위치시킬 때 스타일
export const FormInnerContainerRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-around',
  gap: 10,
}));

/* 주문 그리드 Panel 스타일 */
// 주문 그리드의 상품 상세 그리드(panel) 스타일(최상위)
export const GridDetailPanelContainer = styled(Stack)<{ width?: number }>(({ width, theme }) => ({
  py: theme.spacing(1.5),
  height: '100%',
  boxSizing: 'border-box',
  position: 'sticky',
  left: 0,
  flexDirection: 'column',
  width,
}));

// 주문 그리드의 상품 상세 그리드(panel) 스타일(상위)
export const GridDetailPanelPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  margin: 'auto',
  width: '95%',
  padding: theme.spacing(1),
}));

export const SearchStack = styled(Stack)(({ cols }: { cols: string | number }) => ({
  flexDirection: 'row',
  width: '100%',
  pointerEvents: 'auto',
  display: 'grid',
  gridGap: '4px',
  gridTemplateColumns: `${cols}`,
  wordBreak: 'keep-all',
  alignItems: 'center',
  height: 52,
}));

// 그리드의 버튼을 space-between로 정렬
export const ButtonContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
}));
